"use client"; const PhoneInput = React.lazy(() => import("react-phone-number-input")); import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { toast } from "sonner";
import { useTheme } from "next-themes";
import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import { getTrackingData } from "@/utils/feedback-tracking";
import React from "react";
import { motion } from "framer-motion";
import { Turnstile } from "@marsidev/react-turnstile";
import "react-phone-number-input/style.css";

// Improved emoji ratings with better labels and colors
const emojiRatings = [
    { emoji: "😡", label: "veryDissatisfied", color: "#FF5757" },
    { emoji: "😕", label: "dissatisfied", color: "#FF9B50" },
    { emoji: "😐", label: "neutral", color: "#FFD166" },
    { emoji: "🙂", label: "satisfied", color: "#06D6A0" },
    { emoji: "😄", label: "verySatisfied", color: "#118AB2" }
];

const feedbackSchema = (t: any) => z.object({
    title: z.string().default("Website Feedback"),
    rating: z.number().min(1, t("feedback.ratingRequired")),
    feedback: z.string().min(10, t("feedback.feedbackMinLength")),
    name: z.string().min(1, t("feedback.nameRequired")),
    email: z.string().email(t("feedback.invalidEmail")),
    phone: z.string().min(10, t("feedback.phoneRequired")),
});

type FeedbackDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmitSuccess?: () => void;
};

export default function FeedbackDialog({
    open,
    onOpenChange,
    onSubmitSuccess
}: FeedbackDialogProps) {
    const locale = useLocale();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const { theme } = useTheme();
    const t = useTranslations('FeedbackDialog');
    const [selectedEmoji, setSelectedEmoji] = useState<number | null>(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    // Form with validation
    const form = useForm<z.infer<ReturnType<typeof feedbackSchema>>>({
        resolver: zodResolver(feedbackSchema(t)),
        defaultValues: {
            title: "Website Feedback",
            rating: 0,
            feedback: "",
            name: "",
            email: "",
            phone: "",
        },
    });

    // Reset form when dialog opens
    useEffect(() => {
        if (open) {
            setIsClosing(false);
        }
    }, [open]);

    // Reset state when dialog fully closes
    useEffect(() => {
        if (!open && !isClosing) {
            // Only reset state when dialog is fully closed
            setTimeout(() => {
                setSelectedEmoji(null);
                setCurrentStep(0);
                setFormSubmitted(false);
                form.reset();
            }, 100); // Small delay to ensure dialog is fully closed
        }
    }, [open, isClosing, form]);

    // Step validation functions
    const validateStep1 = useCallback(() => {
        const rating = form.getValues("rating");
        if (rating < 1) {
            form.setError("rating", { message: t("feedback.ratingRequired") });
            return false;
        }
        return true;
    }, [form, t]);

    const validateStep2 = useCallback(() => {
        const feedback = form.getValues("feedback");
        if (feedback.length < 10) {
            form.setError("feedback", { message: t("feedback.feedbackMinLength") });
            return false;
        }
        return true;
    }, [form, t]);

    // Handle next step
    const goToNextStep = useCallback(() => {
        if (currentStep === 0 && validateStep1()) {
            setCurrentStep(1);
        } else if (currentStep === 1 && validateStep2()) {
            setCurrentStep(2);
        }
    }, [currentStep, validateStep1, validateStep2]);

    // Handle previous step
    const goToPreviousStep = useCallback(() => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    }, [currentStep]);

    // Handle emoji selection
    const handleEmojiClick = useCallback((rating: number) => {
        form.setValue("rating", rating);
        setSelectedEmoji(rating - 1);
    }, [form]);

    // Submit handler
    const onSubmit = useCallback(async (data: any) => {
        if (!turnstileToken) {
            toast.error(t("feedback.turnstileRequired"));
            return;
        }

        try {
            setIsSubmitting(true);
            const trackingData = await getTrackingData();

            const response = await fetch("/api/submit-feedback", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...data,
                    locale,
                    ...trackingData,
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                if (result.requiresVerification) {
                    toast.info(t("feedback.verifyEmail") || "Silakan verifikasi email Anda.");
                } else {
                    throw new Error(result.error || t("feedback.submitError") || "Gagal mengirimkan feedback.");
                }
            } else {
                setFormSubmitted(true);
                setCurrentStep(3); // Move to success step
                toast.success(t("feedback.successMessage") || "Feedback Anda telah dikirim. Terima kasih!");
            }
        } catch (error) {
            console.error("Error:", error);
            if (error instanceof Error) {
                toast.error(error.message);
            } else {
                toast.error(t("feedback.unknownError") || "An unknown error occurred.");
            }
        } finally {
            setIsSubmitting(false);
        }
    }, [turnstileToken, t, locale]);

    // Safe close handler
    const handleClose = useCallback(() => {
        setIsClosing(true);
        // Use small timeout to prevent state updates during unmount
        setTimeout(() => {
            onOpenChange(false);
            if (formSubmitted) {
                onSubmitSuccess?.();
            }
        }, 10);
    }, [formSubmitted, onOpenChange, onSubmitSuccess]);

    // Animation variants
    const slideVariants = {
        hidden: { opacity: 0, x: 20 },
        visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
        exit: { opacity: 0, x: -20, transition: { duration: 0.2 } }
    };

    // Form step content
    const renderStepContent = () => {
        switch (currentStep) {
            case 0: // Rating step
                return (
                    <motion.div
                        key="step-0"
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={slideVariants}
                        className="space-y-6"
                    >
                        <FormField
                            control={form.control}
                            name="rating"
                            render={({ field }) => (
                                <FormItem className="space-y-4">
                                    <FormLabel className="text-lg font-semibold">
                                        {t("feedback.ratingLabel") || "Bagaimana pengalaman Anda?"}
                                    </FormLabel>
                                    <FormControl>
                                        <div className="flex flex-col items-center space-y-6">
                                            {/* Emoji rating selector */}
                                            <div className="flex justify-between items-center w-full max-w-md mx-auto p-2">
                                                {emojiRatings.map((rating, index) => (
                                                    <button
                                                        key={`emoji-${index}`}
                                                        type="button"
                                                        onClick={() => handleEmojiClick(index + 1)}
                                                        className={`relative group flex flex-col items-center w-14 h-14 rounded-full 
                              ${selectedEmoji === index ? 'ring-2 ring-offset-2' : ''}
                              transition-all duration-200 hover:scale-110 active:scale-95`}
                                                        style={{
                                                            backgroundColor: selectedEmoji === index ? `${rating.color}20` : 'transparent',
                                                            boxShadow: selectedEmoji === index ? `0 0 12px ${rating.color}40` : 'none',
                                                            borderColor: rating.color
                                                        }}
                                                    >
                                                        <span className="text-3xl" role="img" aria-label={rating.label}>
                                                            {rating.emoji}
                                                        </span>

                                                        {/* Hover label */}
                                                        <div
                                                            className="absolute -bottom-7 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                                        >
                                                            <span
                                                                className="text-xs font-medium px-2 py-1 rounded-full text-white whitespace-nowrap"
                                                                style={{ backgroundColor: rating.color }}
                                                            >
                                                                {t(`feedback.ratings.${rating.label}`) || rating.label}
                                                            </span>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>

                                            {/* Selected feedback label */}
                                            {selectedEmoji !== null && (
                                                <motion.div
                                                    key={`selected-emoji-${selectedEmoji}`}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className="text-center"
                                                >
                                                    <div
                                                        className="inline-block font-medium px-4 py-2 rounded-full text-white"
                                                        style={{ backgroundColor: emojiRatings[selectedEmoji].color }}
                                                    >
                                                        {t(`feedback.ratings.${emojiRatings[selectedEmoji].label}`) || emojiRatings[selectedEmoji].label}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </div>
                                    </FormControl>
                                    <FormMessage className="text-center" />
                                </FormItem>
                            )}
                        />
                    </motion.div>
                );

            case 1: // Feedback step
                return (
                    <motion.div
                        key="step-1"
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={slideVariants}
                        className="space-y-6"
                    >
                        <FormField
                            control={form.control}
                            name="feedback"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-lg font-semibold">
                                        {t("feedback.feedbackLabel") || "Ceritakan pengalaman Anda"}
                                    </FormLabel>
                                    <div className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                        {t("feedback.feedbackDescription") || "Bantu kami meningkatkan layanan dengan berbagi pengalaman Anda"}
                                    </div>
                                    <FormControl>
                                        <Textarea
                                            placeholder={t("feedback.feedbackPlaceholder") || "Tuliskan feedback Anda di sini..."}
                                            className="min-h-[180px] resize-none text-base p-4 bg-gray-50 dark:bg-gray-800/50 focus:ring-2 transition-all duration-200"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </motion.div>
                );

            case 2: // Contact info step
                return (
                    <motion.div
                        key="step-2"
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={slideVariants}
                        className="space-y-6 overflow-y-auto max-h-[60vh] pb-16 sm:pb-0 pr-2"
                    >
                        <div className="space-y-4">
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-base font-medium">
                                            {t("feedback.nameLabel") || "Nama"}
                                            <span className="text-red-500 ml-1">*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder={t("feedback.namePlaceholder") || "Masukkan nama Anda"}
                                                className="bg-gray-50 dark:bg-gray-800/50 h-12 text-base"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-base font-medium">
                                            {t("feedback.emailLabel") || "Email"}
                                            <span className="text-red-500 ml-1">*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                type="email"
                                                placeholder={t("feedback.emailPlaceholder") || "Masukkan email Anda"}
                                                className="bg-gray-50 dark:bg-gray-800/50 h-12 text-base"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="phone"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-base font-medium">
                                            {t("feedback.phoneLabel") || "Nomor Telepon"}
                                            <span className="text-red-500 ml-1">*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-md">
                                                <PhoneInput
                                                    international
                                                    defaultCountry="ID"
                                                    {...field}
                                                    className="w-full h-12 text-base"
                                                />
                                            </div>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* Turnstile captcha */}
                        <div className="pt-2">
                            <Turnstile
                                siteKey="0x4AAAAAAA29-0PYk7u7JLd5"
                                onSuccess={(token) => setTurnstileToken(token)}
                            />
                        </div>
                    </motion.div>
                );

            case 3: // Success step
                return (
                    <motion.div
                        key="step-3"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex flex-col items-center text-center py-6 space-y-4"
                    >
                        <div
                            className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-2"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-10 h-10 text-green-600 dark:text-green-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>

                        <h3 className="text-xl font-bold">{t("feedback.thankYou") || "Terima Kasih!"}</h3>
                        <p className="text-gray-600 dark:text-gray-400 max-w-xs">
                            {t("feedback.successMessage") || "Feedback Anda telah dikirim. Kami menghargai waktu dan masukan Anda."}
                        </p>

                        <Button
                            onClick={handleClose}
                            className="mt-4"
                            style={{
                                backgroundColor: selectedEmoji !== null ? emojiRatings[selectedEmoji].color : undefined
                            }}
                        >
                            {t("feedback.close") || "Tutup"}
                        </Button>
                    </motion.div>
                );
        }
    };


    return (
        <Dialog open={open} onOpenChange={isOpen => {
            if (!isOpen) {
                handleClose();
            } else {
                onOpenChange(true);
            }
        }}>
            <DialogContent className="sm:max-w-[500px] p-0 rounded-xl overflow-hidden w-[95vw] mx-auto max-h-[90vh] flex flex-col">
                {/* Title invisble for accessibility/screen readers */}
                <DialogTitle className="sr-only">
                    {t("feedback.title") || "Feedback Form"}
                </DialogTitle>

                {/* Progress bar for steps */}
                {currentStep < 3 && (
                    <div className="w-full h-1 bg-gray-100 dark:bg-gray-800 flex-shrink-0">
                        <div
                            className="h-full transition-all duration-300"
                            style={{
                                width: `${((currentStep + 1) / 3) * 100}%`,
                                backgroundColor: selectedEmoji !== null ? emojiRatings[selectedEmoji].color : '#6b7280'
                            }}
                        />
                    </div>
                )}

                {/* Close button */}
                <button
                    onClick={handleClose}
                    className="absolute right-4 top-4 rounded-full p-1.5 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors z-10"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>

                {/* Form Content */}
                <div className="px-6 py-5 flex flex-col flex-grow overflow-hidden">
                    {/* Header */}
                    {currentStep < 3 && (
                        <div className="mb-6 flex-shrink-0">
                            <h2 className="text-xl font-bold">
                                {currentStep === 0 && (t("feedback.stepRating") || "Berikan Penilaian")}
                                {currentStep === 1 && (t("feedback.stepFeedback") || "Berikan Feedback")}
                                {currentStep === 2 && (t("feedback.stepContact") || "Informasi Kontak")}
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {t("feedback.stepOf", { current: currentStep + 1, total: 3 }) || `Langkah ${currentStep + 1} dari 3`}
                            </p>
                        </div>
                    )}

                    {/* Form */}
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 flex flex-col flex-grow overflow-hidden">
                            {/* Scrollable content area */}
                            <div className="flex-grow overflow-auto">
                                {renderStepContent()}
                            </div>

                            {/* Navigation buttons - fixed at bottom for mobile */}
                            {currentStep < 3 && (
                                <div className={`flex ${currentStep === 0 ? 'justify-end' : 'justify-between'} pt-2 bg-white dark:bg-gray-950 flex-shrink-0 ${currentStep === 2 ? 'sticky bottom-0 -mx-6 px-6 pb-4 pt-3 border-t border-gray-100 dark:border-gray-800 mt-4' : ''}`}>
                                    {currentStep > 0 && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={goToPreviousStep}
                                            className="gap-1"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="15 18 9 12 15 6"></polyline>
                                            </svg>
                                            {t("feedback.back") || "Kembali"}
                                        </Button>
                                    )}

                                    {currentStep < 2 ? (
                                        <Button
                                            type="button"
                                            onClick={goToNextStep}
                                            className="gap-1"
                                            style={{
                                                backgroundColor: "blue-50"
                                            }}
                                        >
                                            {t("feedback.next") || "Lanjut"}
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="9 18 15 12 9 6"></polyline>
                                            </svg>
                                        </Button>
                                    ) : (
                                        <Button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="gap-2"
                                            style={{
                                                backgroundColor: "blue-50"
                                            }}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    {t("feedback.submitting") || "Mengirim..."}
                                                </>
                                            ) : (
                                                <>
                                                    {t("feedback.submit") || "Kirim Feedback"}
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <line x1="22" y1="2" x2="11" y2="13"></line>
                                                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                                    </svg>
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </div>
                            )}
                        </form>
                    </Form>
                </div>
            </DialogContent>
        </Dialog>
    );
}